/**
 * Delegated, document-level listeners that power the quick-buy modal (app-block script revival, the live add-to-cart price, and showing only "Add to cart" OR only the dynamic checkout button,
 * depending on whether the modal was opened via the sticky bar's "Add to cart" or "Buy now" trigger) and the
 * sticky bar's "Buy now" button (opens the modal for variant/subscription products, or forwards the click to the
 * real dynamic checkout button for single-variant products). The quick-buy modal can be opened from any page
 * (product cards on collection/home pages, the PDP sticky bar, etc.), so this must be loaded globally
 * (layout/theme.liquid) rather than from main-product.liquid - otherwise these listeners are only bound on pages
 * where the "main-product" section happens to render, and the modal's interactive bits silently stop working
 * everywhere else.
 */

function applyQuickBuyNowIntent(modal) {
  const intent = modal.dataset.buyNowIntent;

  // Only set (to 'true' or 'false') when opened via the PDP sticky bar's own "Buy now" / "Add to cart" triggers -
  // see the aria-controls click listener below. Left unset for any other trigger (e.g. a product card's "Choose
  // options"), so this only ever swaps buttons for the PDP sticky bar, never for product-card quick views.
  if (intent !== 'true' && intent !== 'false') {
    return;
  }

  const showBuyNow = intent === 'true';
  const atcButton = modal.querySelector('[data-quick-buy-atc-button]');
  const paymentButton = modal.querySelector('.shopify-payment-button');

  if (atcButton) {
    atcButton.hidden = showBuyNow;
  }

  if (paymentButton) {
    paymentButton.hidden = !showBuyNow;
  }
}

/**
 * Keeps the live total price (unit price x quantity) shown on the add-to-cart button's
 * content in sync with the quantity selector and the subscription panel. The one-time price
 * (data-one-time-price, in the shop's smallest currency unit) is rendered server-side
 * by snippets/buy-buttons.liquid; the subscribed price comes from the panel's selected delivery option.
 * This only recomputes the displayed total on user interaction.
 */
function updateAddToCartPrice(container) {
  const priceElements = container.querySelectorAll('[data-add-to-cart-price]');

  if (priceElements.length === 0) {
    return;
  }

  const subscribeRadio = container.querySelector('[data-subscription-panel-subscribe]');
  const frequency = container.querySelector('[data-subscription-frequency]');
  const subscribePrice = subscribeRadio && subscribeRadio.checked && frequency
    ? parseInt(frequency.options[frequency.selectedIndex].getAttribute('data-price'), 10)
    : NaN;
  const quantityInput = container.querySelector('.quantity-selector__input');
  const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;
  const formatter = new Intl.NumberFormat(Shopify.locale, { style: 'currency', currency: Shopify.currency.active });

  priceElements.forEach(function (priceElement) {
    const oneTimePrice = parseInt(priceElement.getAttribute('data-one-time-price'), 10);
    const unitPrice = isNaN(subscribePrice) ? oneTimePrice : subscribePrice;

    if (isNaN(unitPrice)) {
      return;
    }

    priceElement.textContent = formatter.format((unitPrice * quantity) / 100);
  });
}

/**
 * QuickBuyModal.show() fetches the product page through cachedFetch(), which caches that HTML in memory for the
 * rest of the session (js/common/utilities/cached-fetch.js) - fine for content that never changes, but the cart
 * total baked into the free-shipping bar goes stale the moment the customer adds anything to cart after the first
 * time a given product's quick view was opened. This re-syncs it against the live cart every time the modal opens,
 * before updateFreeShippingBarPreview() runs.
 */
let freeShippingBarSyncToken = 0;

async function refreshFreeShippingBarCartTotal(container) {
  const bar = container.querySelector('free-shipping-bar[data-cart-total-price]');

  if (!bar) {
    return;
  }

  // Several syncs can be in flight together (e.g. rapid variant switching); an older /cart.js response can resolve
  // after a newer one. Only the request started most recently is allowed to write its result.
  const token = ++freeShippingBarSyncToken;

  try {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } });
    const cart = await response.json();
    const shippableTotal = cart.items
      .filter(function (item) { return item.requires_shipping; })
      .reduce(function (sum, item) { return sum + item.final_line_price; }, 0);
    const cartDiscount = (cart.cart_level_discount_applications || [])
      .reduce(function (sum, discount) { return sum + discount.total_allocated_amount; }, 0);

    if (token !== freeShippingBarSyncToken) {
      return;
    }

    bar.setAttribute('data-cart-total-price', shippableTotal - cartDiscount);
  } catch (error) {
    // Network hiccup: fall back to the server-rendered (possibly stale) total already on the element.
  }
}

/**
 * Keeps the quick-buy modal's free-shipping bar reflecting what the order will look like once this product (at its
 * current variant / quantity) is added, not just the cart's current contents. The baseline
 * cart total and the variant's one-time/subscribed unit price (data-cart-total-price / data-one-time-price, in the shop's smallest currency unit) are rendered server-side by
 * snippets/free-shipping-bar.liquid; this only recomputes the previewed total on user interaction.
 */
function updateFreeShippingBarPreview(container) {
  const bar = container.querySelector('free-shipping-bar[data-cart-total-price]');

  if (!bar) {
    return;
  }

  const oneTimePrice = parseInt(bar.getAttribute('data-one-time-price'), 10);

  if (isNaN(oneTimePrice)) {
    return;
  }

  const cartTotalPrice = parseInt(bar.getAttribute('data-cart-total-price'), 10) || 0;
  const unitPrice = oneTimePrice;
  const quantityInput = container.querySelector('.quantity-selector__input');
  const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

  bar.setAttribute('total-price', cartTotalPrice + unitPrice * quantity);
}

function syncFreeShippingBarPreview(container) {
  updateFreeShippingBarPreview(container);
  refreshFreeShippingBarCartTotal(container).then(function () {
    updateFreeShippingBarPreview(container);
  });
}

if (!window.__quickBuyAppBlockScriptsRevived) {
  window.__quickBuyAppBlockScriptsRevived = true;

  document.addEventListener('dialog:after-show', function (event) {
    const modal = event.target;

    if (!(modal instanceof HTMLElement) || modal.tagName.toLowerCase() !== 'quick-buy-modal') {
      return;
    }

    modal.querySelectorAll('script').forEach(function (oldScript) {
      const newScript = document.createElement('script');

      for (const attribute of oldScript.attributes) {
        newScript.setAttribute(attribute.name, attribute.value);
      }

      newScript.textContent = oldScript.textContent;
      oldScript.replaceWith(newScript);
    });

    applyQuickBuyNowIntent(modal);
    updateAddToCartPrice(modal);
    syncFreeShippingBarPreview(modal);

    if (modal.__quickBuyNowObserver) {
      modal.__quickBuyNowObserver.disconnect();
    }

    modal.__quickBuyNowObserver = new MutationObserver(function (mutations) {
      applyQuickBuyNowIntent(modal);

      // Setting free-shipping-bar's total-price/data-cart-total-price attributes, and the add-to-cart price span's
      // textContent, below causes each of them to rewrite their own children (a childList mutation), which this same
      // observer would otherwise see and react to again - an infinite feedback loop. Skip when every mutation in
      // this batch originated inside one of those two elements; only react to real content swaps (variant changes)
      // coming from outside them.
      const isOwnUpdate = mutations.every(function (mutation) {
        const target = mutation.target;
        return target.closest && (target.closest('free-shipping-bar') || target.closest('[data-add-to-cart-price]'));
      });

      if (isOwnUpdate) {
        return;
      }

      // The modal's content is fully swapped (not patched) on every variant change - see product-rerender.js. The
      // variant picker also preloads every other combination's HTML as soon as it becomes visible (5-minute cache in
      // variant-picker.js), baking in whatever the cart total was at that moment. So each swap can silently bring
      // back a stale cart total, not just the very first render this dialog:after-show handles. buy-buttons.liquid
      // also always server-renders the plain one-time price text so the button price needs the same re-sync.
      updateAddToCartPrice(modal);
      syncFreeShippingBarPreview(modal);
    });

    modal.__quickBuyNowObserver.observe(modal, { childList: true, subtree: true });
  }, true);
}

/**
 * Subscription panel block (snippets/subscription-panel.liquid): the selected card (one time / subscribe) and the
 * delivery frequency drive the form's `selling_plan` field and the displayed discount/prices. Delegated, since product-rerender replaces the panel markup on every variant change.
 */
if (!window.__subscriptionPanelBound) {
  window.__subscriptionPanelBound = true;

  document.addEventListener('change', function (event) {
    const panel = event.target.closest('[data-subscription-panel]');

    if (!panel || !event.target.matches('[data-subscription-option], [data-subscription-frequency]')) {
      return;
    }

    const isSubscribed = panel.querySelector('[data-subscription-option="subscribe"]').checked;
    const frequency = panel.querySelector('[data-subscription-frequency]');
    const plan = frequency.options[frequency.selectedIndex];
    const hasDiscount = plan.hasAttribute('data-has-discount');
    const compare = panel.querySelector('[data-subscription-compare]');
    const percentWrapper = panel.querySelector('[data-subscription-percent-wrapper]');

    panel.querySelectorAll('[data-subscription-card]').forEach(function (card) {
      card.classList.toggle('is-selected', (card.getAttribute('data-subscription-card') === 'subscribe') === isSubscribed);
    });

    panel.querySelector('[data-subscription-input]').value = isSubscribed ? plan.value : '';
    panel.querySelector('[data-subscription-price]').textContent = plan.getAttribute('data-price-formatted');
    panel.querySelector('[data-subscription-percent]').textContent = plan.getAttribute('data-percent');
    compare.textContent = plan.getAttribute('data-compare-formatted');
    compare.hidden = !hasDiscount;
    percentWrapper.hidden = !hasDiscount;

    updateAddToCartPrice(panel.closest('product-rerender') || document);
  });

  document.addEventListener('click', function (event) {
    const perksToggle = event.target.closest('[data-subscription-perks-toggle]');

    if (!perksToggle) {
      return;
    }

    const perks = perksToggle.closest('[data-subscription-card]').querySelector('[data-subscription-perks]');
    const isOpen = perksToggle.getAttribute('aria-expanded') === 'true';

    perksToggle.setAttribute('aria-expanded', String(!isOpen));
    perks.hidden = isOpen;
  });
}

if (!window.__addToCartPriceQuantityBound) {
  window.__addToCartPriceQuantityBound = true;

  ['input', 'change'].forEach(function (eventName) {
    document.addEventListener(eventName, function (event) {
      const quantityInput = event.target.closest('.quantity-selector__input');

      if (!quantityInput) {
        return;
      }

      const scope = quantityInput.closest('product-rerender') || document;
      updateAddToCartPrice(scope);
      updateFreeShippingBarPreview(scope);
    });
  });
}

if (!window.__quickBuyNowIntentBound) {
  window.__quickBuyNowIntentBound = true;

  document.addEventListener('click', function (event) {
    const trigger = event.target.closest('[aria-controls]');

    if (!trigger) {
      return;
    }

    const modal = document.getElementById(trigger.getAttribute('aria-controls'));

    if (!(modal instanceof HTMLElement) || modal.tagName.toLowerCase() !== 'quick-buy-modal') {
      return;
    }

    if (trigger.hasAttribute('data-buy-now-trigger')) {
      modal.dataset.buyNowIntent = 'true';
    } else if (trigger.hasAttribute('data-atc-trigger')) {
      modal.dataset.buyNowIntent = 'false';
    } else {
      delete modal.dataset.buyNowIntent;
    }
  });
}

/**
 * The sticky bar's "Buy now" button (single-variant products, no quick-buy modal) doesn't submit anything itself -
 * it forwards the click to the real dynamic checkout button (Shop Pay / PayPal / "More payment options") already
 * rendered by snippets/buy-buttons.liquid via the `payment_button` filter, inside the same product form elsewhere
 * on the page (data-dynamic-checkout-trigger holds that form's id). That widget's markup/timing is owned by
 * Shopify, not this theme, so this only works once Shopify's script has populated it with a clickable element.
 */
if (!window.__dynamicCheckoutForwardBound) {
  window.__dynamicCheckoutForwardBound = true;

  document.addEventListener('click', function (event) {
    const trigger = event.target.closest('[data-dynamic-checkout-trigger]');

    if (!trigger) {
      return;
    }

    const formId = trigger.getAttribute('data-dynamic-checkout-trigger');
    const container = formId && document.getElementById(formId)?.querySelector('.shopify-payment-button');
    const target = container?.querySelector('button, [role="button"], a');

    if (target) {
      target.click();
    }
  });
}
